<template>
  <auth-shell>
    <p class="auth-form__eyebrow">
      Un paso más
    </p>
    <h1 class="auth-form__title auth-form__title--with-lead">
      Completa tu cuenta
    </h1>
    <p class="auth-form__lead">
      {{ lead }}
    </p>

    <b-form
      class="auth-form__fields"
      @submit.prevent="submit"
    >
      <div v-if="needsPhone">
        <label
          class="sr-only"
          for="complete-phone"
        >WhatsApp</label>
        <b-input-group :class="['auth-input', { 'auth-input--invalid': phoneError }]">
          <b-input-group-prepend is-text>
            <feather-icon icon="PhoneIcon" />
          </b-input-group-prepend>
          <b-form-input
            id="complete-phone"
            v-model="phone"
            autocomplete="tel-national"
            type="tel"
            inputmode="numeric"
            maxlength="10"
            placeholder="WhatsApp a 10 dígitos"
            @input="sanitizePhone"
          />
        </b-input-group>
        <small
          v-if="phoneError"
          class="text-danger"
        >{{ phoneError }}</small>
        <small
          v-else
          class="auth-form__hint"
        >Lo usamos para avisarte de tus cotizaciones, pagos y entregas.</small>
      </div>

      <legal-consent
        v-if="needsLegal"
        v-model="acceptLegal"
        input-id="complete-legal"
        :error="legalError"
      />

      <p
        v-if="serverError"
        class="text-danger mb-0"
      >
        {{ serverError }}
      </p>

      <b-button
        type="submit"
        class="auth-submit"
        block
        :disabled="saving"
      >
        <b-spinner
          v-if="saving"
          small
        />
        <template v-else>
          <span>Continuar</span>
          <feather-icon
            icon="ArrowRightIcon"
            size="18"
          />
        </template>
      </b-button>
    </b-form>

    <p class="auth-form__footer">
      ¿No eres tú?
      <b-link @click="logout">
        Cerrar sesión
      </b-link>
    </p>
  </auth-shell>
</template>

<script>
import {
  BForm, BFormInput, BInputGroup, BInputGroupPrepend, BButton, BSpinner, BLink,
} from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
import { landingRoute } from '@/auth/session'
import AuthShell from '@/components/auth/AuthShell.vue'
import LegalConsent from '@/components/auth/LegalConsent.vue'

const readUserData = () => {
  try {
    return JSON.parse(localStorage.getItem('userData') || '{}')
  } catch (error) {
    return {}
  }
}

export default {
  components: {
    AuthShell,
    LegalConsent,
    BForm,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BButton,
    BSpinner,
    BLink,
  },

  data() {
    return {
      requirements: readUserData().account_requirements || [],
      phone: '',
      acceptLegal: false,
      phoneError: '',
      legalError: '',
      serverError: '',
      saving: false,
    }
  },

  computed: {
    needsPhone() {
      return this.requirements.includes('phone')
    },
    needsLegal() {
      return this.requirements.includes('legal')
    },
    lead() {
      if (this.needsPhone && this.needsLegal) {
        return 'Necesitamos tu WhatsApp y que aceptes los Términos y el Aviso de Privacidad para que puedas usar YoConstruyo.'
      }
      if (this.needsPhone) return 'Necesitamos tu WhatsApp para avisarte de tus cotizaciones, pagos y entregas.'
      return 'Actualizamos nuestros Términos y Condiciones y el Aviso de Privacidad. Revísalos y acéptalos para continuar.'
    },
  },

  created() {
    if (!this.requirements.length) this.goHome()
  },

  methods: {
    sanitizePhone(value) {
      this.phone = String(value || '').replace(/\D/g, '').slice(0, 10)
      if (this.phoneError && this.phone.length === 10) this.phoneError = ''
    },

    validate() {
      this.phoneError = this.needsPhone && this.phone.length !== 10 ? 'Escribe tu WhatsApp a 10 dígitos' : ''
      this.legalError = this.needsLegal && !this.acceptLegal ? 'Para continuar debes aceptar los Términos y el Aviso de Privacidad' : ''
      return !this.phoneError && !this.legalError
    },

    async submit() {
      this.serverError = ''
      if (!this.validate() || this.saving) return

      this.saving = true
      try {
        const userData = await this.$store.dispatch('legal/completeAccount', {
          phone: this.needsPhone ? this.phone : undefined,
          acceptLegal: this.needsLegal ? this.acceptLegal : undefined,
        })
        this.requirements = userData.account_requirements || []
        if (!this.requirements.length) this.goHome(userData)
      } catch (error) {
        this.serverError = error?.response?.data?.messages?.join('. ') || 'No pudimos guardar tus datos; inténtalo de nuevo'
      } finally {
        this.saving = false
      }
    },

    goHome(userData = readUserData()) {
      this.$router.replace(landingRoute(userData))
    },

    logout() {
      document.documentElement.style.setProperty('--primary-custom', '#340668')
      document.documentElement.style.setProperty('--secondary-custom', '#340668')
      this.$store.commit('auth/clearCurrentUser')
      localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
      localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)
      localStorage.removeItem('userData')
      this.$router.replace({ name: 'auth-login' })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../components/auth/auth-page.scss';
</style>
